import { importProvidersFrom, LOCALE_ID } from '@angular/core';
import { ApplicationConfig } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeDECH from '@angular/common/locales/de-CH';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import { ROUTES } from './app.routes';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { META_REDUCERS, StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';

import { reducers } from '@ebanking/core/store';
import { UserEffects } from '@qvs/core/user';
import { EnvEffects } from '@qvs/core/env';
import { AuthEffects } from '@qvs/core/auth';
import { PreferenceEffects } from '@qvs/core/preference';
import { RemoteConfigEffects } from '@qvs/core/remoteConfig';

import * as wizeMobileApi from '@wize-mobile-api';
import * as chatApi from '@chat-api';
import * as authenticationApi from '@authentication-api';
import * as tcAccountApi from '@topcard-account-api';
import * as tcTransactionApi from '@topcard-transaction-api';
import * as newsApi from '@news-api';
import {
  buildSpecificImports,
  HttpRequestTimeoutInterceptor,
  persistenceMetaReducerFactory,
  resetMetaReducerFactory,
  RetryInterceptor,
  SessionTimeoutInterceptor
} from '@qvs/core';
import { PersistenceService } from '~/shared/services/persistence.service';

import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getRemoteConfig, provideRemoteConfig } from '@angular/fire/remote-config';
import { firebaseConfig } from '@environments/firebaseConfig';
import { PortfolioEffects } from '~/modules/portfolio/portfolio.effects';
import { MandateTypeEffects } from '~/modules/mandateTypes/mandateType.effects';
import { AccountEffects } from '~/modules/accounts/account.effects';
import { CountryEffects } from '~/modules/countries/country.effects';
import { CurrenciesEffects } from '~/modules/currencies/currencies.effects';
import { ExchangeRatesEffects } from '~/modules/exchangeRates/exchangeRates.effects';
import { NewsEffects } from '~/modules/news/news.effects';
import { EDocumentsEffects } from '~/modules/e-documents/e-documents.effects';
import { RegistrationEffects } from '@qvs/core/registration';
import { PaymentsEffects } from '~/modules/payments/payments.effects';
import { InstrumentEffects } from '~/modules/instrument/instrument.effects';
import { InvestmentOrderEffects } from '~/modules/investmentOrder/investment-order.effects';
import { RouterHelper } from '~/shared/helpers/router-helper';
import { ModalControllerService } from '~/shared/services/modal-controller.service';
import { windowProvider, windowToken } from '~/shared/helpers/window-helper';

registerLocaleData(localeDECH);

export const httpTranslateLoaderProvider = provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' });

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(ROUTES),
    provideHttpClient(withInterceptorsFromDi()),
    provideTranslateService({
      loader: httpTranslateLoaderProvider
    }),
    provideFirebaseApp(() => initializeApp(firebaseConfig, { automaticDataCollectionEnabled: false })),
    provideRemoteConfig(() => getRemoteConfig()),
    importProvidersFrom(
      wizeMobileApi.ApiModule,
      chatApi.ApiModule,
      authenticationApi.ApiModule,
      tcAccountApi.ApiModule,
      tcTransactionApi.ApiModule,
      newsApi.ApiModule,
      BrowserAnimationsModule,
      IonicModule.forRoot({
        mode: 'ios'
      }),
      StoreModule.forRoot(reducers, {
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          strictActionTypeUniqueness: true,
          strictActionWithinNgZone: true
        }
      }),
      EffectsModule.forRoot([]),
      EffectsModule.forFeature([
        EnvEffects,
        AuthEffects,
        RemoteConfigEffects,
        UserEffects,
        PreferenceEffects,
        PortfolioEffects,
        MandateTypeEffects,
        NewsEffects,
        EDocumentsEffects,
        AccountEffects,
        CountryEffects,
        CurrenciesEffects,
        ExchangeRatesEffects,
        RegistrationEffects,
        //ChatEffects,
        //CardEffects,
        //CardTransactionEffects,
        //AnalyticalCodeEffects,
        PaymentsEffects,
        //CalendarEffects,
        //DocumentEffects,
        InstrumentEffects,
        //SixMarketDataEffects,
        InvestmentOrderEffects
        //NewInvestmentOrderEffects,
      ]),

      buildSpecificImports
    ),
    RouterHelper,
    ModalControllerService,
    { provide: windowToken, useFactory: windowProvider },
    { provide: LOCALE_ID, useValue: 'de-ch' },
    { provide: HTTP_INTERCEPTORS, useClass: RetryInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpRequestTimeoutInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SessionTimeoutInterceptor, multi: true },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy }, // TODO 04-Jun-2026/braune: check if needed

    { provide: META_REDUCERS, deps: [PersistenceService], useFactory: resetMetaReducerFactory, multi: true },
    { provide: META_REDUCERS, deps: [PersistenceService], useFactory: persistenceMetaReducerFactory, multi: true }
  ]
};
